/**
 * Chunk 1 — Credit Requests table helpers (display honesty + richer columns).
 * Maps credits-history API rows → table records. No schema changes.
 */

import { formatGbp } from './formatGbp'
import { CREDIT_REASONS, creditMoney } from './invoiceCreditDiscrepancy'

export const CREDIT_REQUEST_DISPLAY = {
  unknownSku: '—',
  unknownProduct: 'Unknown product',
  unknownAmount: '—',
  unknownNotes: '—',
  unknownReason: '—',
  unknownSource: 'Unknown',
  unknownQty: '—',
  blankIdentityTooltip:
    'Product details were missing on the order line — showing the catalogue product when available.',
  missingAmountTooltip:
    'No unit price on this line — est. credit cannot be calculated.',
}

const EDIT_TYPE_LABELS = {
  credit_note_request: 'Credit asked',
  invoice_reconciliation: 'Receipt issue (no credit asked)',
}

/**
 * Lifecycle: Credit asked → Supplier approved/rejected → CN applied → Resolved
 */
export function getCreditRequestLifecycleChips({
  statusText,
  statusColor,
  creditNoteApplied = false,
  resolved = false,
} = {}) {
  const chips = []
  if (statusText) {
    chips.push({
      key: 'stage',
      label: statusText,
      color: statusColor || 'default',
    })
  }
  if (creditNoteApplied && !resolved) {
    chips.push({
      key: 'cn_applied',
      label: 'CN applied',
      color: 'orange',
    })
  }
  if (resolved) {
    chips.push({
      key: 'resolved',
      label: 'Resolved',
      color: 'green',
    })
  }
  return chips
}

export function getCreditRequestFollowUpHint({
  statusText,
  creditNoteApplied = false,
  resolved = false,
} = {}) {
  if (resolved) return null
  if (creditNoteApplied) {
    return {
      kind: 'applied',
      text: 'CN applied — mark resolved?',
      tooltip:
        'A credit note is already applied to this PO — mark resolved if the request is dealt with.',
    }
  }
  if (statusText === 'Supplier approved') {
    return {
      kind: 'approved',
      text: 'Upload CN or mark resolved',
      tooltip:
        'Supplier approved — upload the Credit Note PDF (Invoices & Credit Notes) or mark resolved if you no longer need it.',
    }
  }
  if (statusText === 'Credit asked' || statusText === 'CN Requested') {
    return {
      kind: 'chase',
      text: 'Waiting on credit note or supplier',
      tooltip:
        'Still waiting? Chase the supplier, or upload the credit note when it arrives.',
    }
  }
  return null
}

function isPresentString(value) {
  return value != null && String(value).trim() !== ''
}

function firstPresentString(...values) {
  for (const value of values) {
    if (isPresentString(value)) return String(value).trim()
  }
  return null
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const num = Number(value)
    if (Number.isFinite(num)) return num
  }
  return null
}

/**
 * Coalesce identity: Order → receipting ordered* → product (when API enriched).
 */
export function coalesceCreditRequestIdentity(orderNote = {}) {
  const id = orderNote._id || {}
  const receipting = id.receipting?.[0] || orderNote.receipting?.[0] || {}
  const product = id.product?.[0] || id.productDoc || orderNote.product || {}

  const sku =
    firstPresentString(
      id.sku,
      receipting.orderedSku,
      receipting.updatedSku,
      product.sku,
    ) || CREDIT_REQUEST_DISPLAY.unknownSku

  const title =
    firstPresentString(
      id.title,
      receipting.orderedTitle,
      receipting.updatedTitle,
      product.title,
    ) || CREDIT_REQUEST_DISPLAY.unknownProduct

  const pricePennies = firstFiniteNumber(
    id.price,
    receipting.orderedPrice,
    receipting.updatedPrice,
    product.price,
  )

  const usedFallback =
    !isPresentString(id.sku) ||
    !isPresentString(id.title) ||
    id.price == null ||
    id.price === ''

  return { sku, title, pricePennies, usedFallback }
}

export function formatCreditRequestAmount(pricePennies, quantity) {
  if (pricePennies == null || pricePennies === '') {
    return CREDIT_REQUEST_DISPLAY.unknownAmount
  }
  if (quantity == null || quantity === '') {
    return CREDIT_REQUEST_DISPLAY.unknownAmount
  }
  const price = Number(pricePennies)
  const qty = Number(quantity)
  if (!Number.isFinite(price) || !Number.isFinite(qty)) {
    return CREDIT_REQUEST_DISPLAY.unknownAmount
  }
  return (Math.round(price * qty) / 100).toFixed(2)
}

/**
 * Table cell for "Est. credit": line amounts are plain "111.98" (the CSV keeps
 * them that way), so add £ and the thousands comma here. Invoice-wide amounts
 * already come back as "£1,111.98" and "—" stays "—".
 */
export function formatCreditRequestAmountCell(amount) {
  if (amount == null || amount === '')
    return CREDIT_REQUEST_DISPLAY.unknownAmount
  const text = String(amount)
  if (text === CREDIT_REQUEST_DISPLAY.unknownAmount || /£/.test(text)) {
    return text
  }
  if (!Number.isFinite(Number(text))) return text
  return formatGbp(text)
}

export function getCreditRequestStatusMeta(receipting = {}) {
  const editType = receipting.editType
  const cnRequestStatus = receipting.cnRequestStatus
  const rawStatus = String(receipting.status || '').toLowerCase()

  if (editType === 'invoice_reconciliation') {
    return {
      statusText: EDIT_TYPE_LABELS.invoice_reconciliation,
      statusColor: 'blue',
      statusTooltip:
        'This line was reported on goods receipting without Ask for credit. Chase the delivery issue on Goods receipting, or mark resolved if done.',
    }
  }

  if (editType === 'credit_note_request') {
    switch (cnRequestStatus) {
      case 'approved':
        return {
          statusText: 'Supplier approved',
          statusColor: 'green',
          statusTooltip: 'Supplier approved the credit request.',
        }
      case 'rejected':
        return {
          statusText: 'Supplier rejected',
          statusColor: 'red',
          statusTooltip: 'Supplier rejected the credit request.',
        }
      case 'fulfilled':
      case 'resolved':
        return {
          statusText: 'CN Fulfilled',
          statusColor: 'green',
          statusTooltip: 'Credit request marked fulfilled.',
        }
      default:
        return {
          statusText: 'Credit asked',
          statusColor: 'orange',
          statusTooltip: 'Waiting on the supplier or a credit note PDF.',
        }
    }
  }

  if (rawStatus.includes('credit approved')) {
    return {
      statusText: 'Supplier approved',
      statusColor: 'green',
      statusTooltip: 'Supplier approved the credit request.',
    }
  }
  if (rawStatus.includes('rejected')) {
    return {
      statusText: 'Supplier rejected',
      statusColor: 'red',
      statusTooltip: 'Supplier rejected the credit request.',
    }
  }
  if (rawStatus.includes('replacement')) {
    return {
      statusText: 'Replacement Sent',
      statusColor: 'green',
      statusTooltip: 'Supplier sent a replacement.',
    }
  }
  if (rawStatus.includes('sent to supplier')) {
    return {
      statusText: 'Credit asked',
      statusColor: 'orange',
      statusTooltip: 'Waiting on the supplier or a credit note PDF.',
    }
  }

  if (editType && EDIT_TYPE_LABELS[editType]) {
    return {
      statusText: EDIT_TYPE_LABELS[editType],
      statusColor: 'blue',
      statusTooltip: 'Receipting follow-up.',
    }
  }

  return {
    statusText: 'Pending',
    statusColor: 'default',
    statusTooltip: 'Credit request status pending.',
  }
}

export function getCreditRequestSourceLabel(orderNote = {}, receipting = {}) {
  const placedVia = firstPresentString(
    receipting.placedVia,
    orderNote._id?.placedVia,
    orderNote.placedVia,
  )
  if (!placedVia) return CREDIT_REQUEST_DISPLAY.unknownSource

  const lower = placedVia.toLowerCase()
  if (lower === 'dashboard' || lower.includes('dashboard')) return 'Dashboard'
  if (lower === 'mobile' || lower.includes('app')) return 'App'
  if (lower.includes('auto')) return 'Auto order'
  // Human name in placedVia (legacy) — treat as person, not channel
  return placedVia
}

/**
 * Prefer explicit requestedBy; fall back to human placedVia when it isn't a channel label.
 */
export function getCreditRequestRequestedBy(orderNote = {}, receipting = {}) {
  const explicit = firstPresentString(receipting.requestedBy)
  if (explicit) return explicit

  const placedVia = firstPresentString(
    receipting.placedVia,
    orderNote._id?.placedVia,
    orderNote.placedVia,
  )
  if (!placedVia) return null
  const lower = placedVia.toLowerCase()
  if (
    lower === 'dashboard' ||
    lower === 'mobile' ||
    lower.includes('auto order') ||
    lower === 'app'
  ) {
    return null
  }
  // Names like "Stephen Scurr" or emails were historically stored in placedVia
  return placedVia
}

export function formatOrderedToCreditQty(orderedQty, creditQty) {
  const ordered = firstFiniteNumber(orderedQty)
  const credit = firstFiniteNumber(creditQty)
  if (ordered == null && credit == null)
    return CREDIT_REQUEST_DISPLAY.unknownQty
  if (ordered == null) return String(credit)
  if (credit == null) return `${ordered} → —`
  return `${ordered} → ${credit}`
}

export function truncateCreditRequestNotes(notes, maxLen = 48) {
  if (!isPresentString(notes)) {
    return {
      display: CREDIT_REQUEST_DISPLAY.unknownNotes,
      full: '',
      hasNotes: false,
    }
  }
  const full = String(notes).trim()
  if (full.length <= maxLen) {
    return { display: full, full, hasNotes: true }
  }
  return {
    display: `${full.slice(0, maxLen).trim()}…`,
    full,
    hasNotes: true,
  }
}

/**
 * Map one credits-history API row → Credit Requests table record.
 */
export function mapCreditHistoryRowToTableRecord(
  orderNote = {},
  { creditNoteApplied = false, invoices = [] } = {},
) {
  const id = orderNote._id || {}
  const receipting = id.receipting?.[0] || {}
  // Invoice disputes are amounts, not received quantity × unit price.
  // Do not borrow another invoice's credit simply because its PO matches.
  const invoiceId = receipting.invoice?._id || receipting.invoice
  if (invoiceId) {
    const invoice = invoices.find(
      row => String(row.id || row._id) === String(invoiceId),
    )
    const credit = invoice?.creditDiscrepancy
    return {
      PON: id.purchaseOrderNumber || '—',
      SKU: '—',
      productTitle: 'Invoice discrepancy',
      supplierTitle: firstPresentString(id.supplierTitile?.[0]) || '—',
      invoiceId: String(invoiceId),
      invoice,
      quantity: null,
      orderedQuantity: null,
      qtyDisplay: '—',
      amount: credit ? creditMoney(credit.amountRequestedPennies) : '—',
      amountMissing: !credit,
      reason: CREDIT_REASONS[credit?.reason] || 'Invoice discrepancy',
      notesDisplay: credit?.notes || receipting.notes || '—',
      notesFull: credit?.notes || receipting.notes || '',
      source: 'Invoice discrepancy',
      requestedBy: credit?.requestedByName || receipting.requestedBy || '—',
      requestedAt: credit?.requestedAt || null,
      createdAt: id.orderedDate,
      statusText:
        credit?.status === 'resolved'
          ? 'Requested credit received'
          : 'Review invoice credit',
      statusColor: 'orange',
      followUpHint: {
        text: credit
          ? `${creditMoney(credit.outstandingPennies)} request outstanding`
          : 'Open the invoice to check credit amounts.',
        tooltip:
          'This is an invoice-wide request; amounts are not per product line. Review the remaining invoice difference before reconciliation.',
      },
      resolved: false,
      receiptingId: receipting._id,
    }
  }
  const identity = coalesceCreditRequestIdentity(orderNote)

  const creditQty = firstFiniteNumber(receipting.updatedQuantity, id.quantity)
  const orderedQty = firstFiniteNumber(receipting.orderedQuantity, id.quantity)

  const statusMeta = getCreditRequestStatusMeta(receipting)
  const notesMeta = truncateCreditRequestNotes(receipting.notes)
  const reason =
    firstPresentString(receipting.error) || CREDIT_REQUEST_DISPLAY.unknownReason
  const requestedAt = receipting.createdAt || id.orderedDate || null
  const resolved = receipting.orderResolved === true
  const applied = Boolean(creditNoteApplied)
  const lifecycleChips = getCreditRequestLifecycleChips({
    statusText: statusMeta.statusText,
    statusColor: statusMeta.statusColor,
    creditNoteApplied: applied,
    resolved,
  })
  const followUpHint = getCreditRequestFollowUpHint({
    statusText: statusMeta.statusText,
    creditNoteApplied: applied,
    resolved,
  })

  return {
    PON: id.purchaseOrderNumber || '—',
    SKU: identity.sku,
    productTitle: identity.title,
    identityFallback: identity.usedFallback,
    supplierTitle: firstPresentString(id.supplierTitile?.[0]) || '—',
    quantity: creditQty,
    orderedQuantity: orderedQty,
    qtyDisplay: formatOrderedToCreditQty(orderedQty, creditQty),
    amount: formatCreditRequestAmount(identity.pricePennies, creditQty),
    amountMissing: identity.pricePennies == null || creditQty == null,
    statusText: statusMeta.statusText,
    statusColor: statusMeta.statusColor,
    statusTooltip: statusMeta.statusTooltip,
    lifecycleChips,
    followUpHint,
    reason,
    notesDisplay: notesMeta.display,
    notesFull: notesMeta.full,
    hasNotes: notesMeta.hasNotes,
    source: getCreditRequestSourceLabel(orderNote, receipting),
    requestedBy: getCreditRequestRequestedBy(orderNote, receipting),
    requestedAt,
    reportStatus: receipting.error,
    createdAt: id.orderedDate,
    resolved,
    receiptingId: receipting._id,
    creditNoteApplied: applied,
  }
}

const CHASE_CLOSED = /approved|rejected|fulfilled|received|resolved/i

export function canChaseCreditRequest(record = {}) {
  if (!record.receiptingId || record.resolved) return false
  const status = String(record.statusText || '')
  if (CHASE_CLOSED.test(status)) return false
  return status === 'Credit asked' || status === 'Review invoice credit'
}

export function creditChaseSummary(creditChase, now = new Date()) {
  if (!creditChase?.lastChasedAt) return { chasedToday: false, label: '' }
  const last = new Date(creditChase.lastChasedAt)
  const chasedToday = now.getTime() - last.getTime() < 24 * 60 * 60 * 1000
  const count = Number(creditChase.chaseCount) || 0
  const times = count === 1 ? 'Chased 1 time' : `Chased ${count} times`
  const when = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(last)
  const by = String(creditChase.lastChasedBy || '').trim()
  return {
    chasedToday,
    label: [when, by, times.replace('Chased ', '')].filter(Boolean).join(' · '),
  }
}
